import { Modal, Form, DatePicker, Select, Button, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import React, { useEffect, useState } from 'react'

import { UserService } from '@services/UserService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import { subscriptionsStatuses, subscriptionTerms } from '../../../../types'
import type { Product, User, Subscription } from '../../../../types'

dayjs.extend(weekday)
dayjs.extend(localeData)

interface EditSubscriptionModalProps {
    isOpen: boolean
    onClose: () => void
    products: Product[]
    subscription: Subscription | null // Replace 'any' with the actual Subscription type
    user: User | null
    refetch: () => Promise<void>
}

export const EditSubscriptionModal: React.FC<EditSubscriptionModalProps> = ({
    isOpen,
    onClose,
    user,
    products,
    subscription,
    refetch
}) => {
    const { showNotification } = useNotifications()
    const confirm = useConfirmation()
    const [form] = Form.useForm()
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (subscription && user) {
            form.setFieldsValue({
                status: subscription.status,
                term: subscription.term,
                dateRange: [dayjs(subscription.startDate), dayjs(subscription.endDate)],
                productIds: subscription.subscriptionProducts.map((sp: any) => sp.product.id)
            })
        }
    }, [subscription, form])

    if (!subscription) {
        return null
    }

    const handleSubmit = async (values: any) => {
        if (!user) {
            return
        }
        const formattedValues = {
            startDate: values.dateRange[0].toISOString(),
            endDate: values.dateRange[1].toISOString(),
            userId: user.id,
            productIds: values.productIds || [],
            status: values.status,
            term: values.term
        }

        try {
            setIsLoading(true)
            await UserService.updateSubscription(user.id, subscription.id, formattedValues)
            onClose()
            await refetch()
        } catch (error) {
            showNotification({
                title: 'Error updating subscription',
                type: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    const onCancelSubscription = async () => {
        if (!user) {
            return
        }

        if (
            await confirm({
                title: 'Are you sure you want to cancel the subscription?',

                confirmType: 'danger'
            })
        ) {
            try {
                setIsLoading(true)
                await UserService.cancelSubscription(user.id, subscription.id)
                onClose()
                await refetch()
            } catch (error) {
                showNotification({
                    title: 'Error cancelling subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    const onDeleteSubscription = async () => {
        if (!user) {
            return
        }
        if (
            await confirm({
                title: 'Are you sure you want to delete this subscription?',
                confirmText: 'Delete'
            })
        ) {
            try {
                setIsLoading(true)
                await UserService.deleteSubscription(user.id, subscription.id)
                onClose()
                await refetch()
            } catch (error) {
                showNotification({
                    title: 'Error deleting subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    return (
        <Modal title="Edit Subscription" open={isOpen} onCancel={onClose} footer={null}>
            {subscription.stripeId && <Typography.Text>Stripe Subscriptions must be edited in Stripe </Typography.Text>}

            {!subscription.stripeId && (
                <Form form={form} onFinish={handleSubmit} layout="vertical">
                    <Form.Item
                        name="status"
                        label="Status"
                        initialValue={subscription.status}
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select>
                            {Object.values(subscriptionsStatuses).map(status => (
                                <Select.Option key={status} value={status}>
                                    {status}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="term"
                        label="Term"
                        initialValue={subscription.term}
                        rules={[{ required: true, message: 'Please select a term' }]}
                    >
                        <Select>
                            {Object.values(subscriptionTerms).map(term => (
                                <Select.Option key={term} value={term}>
                                    {term}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="dateRange"
                        label="Start & End Date"
                        initialValue={[dayjs(subscription.startDate), dayjs(subscription.endDate)]}
                        rules={[{ required: true, message: 'Please select a start and end date' }]}
                    >
                        <DatePicker.RangePicker />
                    </Form.Item>

                    <Form.Item
                        name="productIds"
                        label="Products"
                        initialValue={subscription.subscriptionProducts.map(sp => sp.product.id)}
                    >
                        <Select mode="multiple">
                            {products.map(product => (
                                <Select.Option key={product.id} value={product.id}>
                                    {product.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
                                Update Subscription
                            </Button>
                            {subscription.status !== 'CANCELLED' && (
                                <>
                                    <Button
                                        type="dashed"
                                        danger
                                        onClick={onCancelSubscription}
                                        disabled={!!subscription.stripeId}
                                    >
                                        Cancel Subscription
                                    </Button>
                                </>
                            )}
                            {subscription.status === 'CANCELLED' && (
                                <Button type="primary" danger onClick={onDeleteSubscription}>
                                    Delete Subscription
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}
        </Modal>
    )
}
